import React from 'react';
import { ArrowDown, GitBranch, Mail, Pause, Square, Tag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { gymSocialMutedText } from '../gymPageTheme';
import {
  formatAutomationConditionLabel,
  formatAutomationStepLabel,
  formatAutomationTriggerSummary,
} from './gymEmailAutomationFormat';

function FlowNode({ icon: Icon, title, subtitle, tone = 'default' }) {
  const tones = {
    default: 'border-indigo-300 bg-indigo-50 dark:border-indigo-400/40 dark:bg-indigo-500/10',
    trigger: 'border-amber-400 bg-amber-50 dark:border-amber-400/50 dark:bg-amber-500/10',
    condition: 'border-sky-300 bg-sky-50 dark:border-sky-400/40 dark:bg-sky-500/10',
    action: 'border-emerald-300 bg-emerald-50 dark:border-emerald-400/40 dark:bg-emerald-500/10',
    stop: 'border-red-300 bg-red-50 dark:border-red-400/40 dark:bg-red-500/10',
  };
  return (
    <div className={`rounded-xl border px-4 py-3 min-w-[200px] max-w-full ${tones[tone] || tones.default}`}>
      <div className="flex items-start gap-2">
        {Icon && <Icon className="h-4 w-4 mt-0.5 shrink-0 text-gray-600 dark:text-white/80" />}
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white">{title}</p>
          {subtitle && <p className={`${gymSocialMutedText} text-xs mt-0.5 break-words`}>{subtitle}</p>}
        </div>
      </div>
    </div>
  );
}

function Connector() {
  return (
    <div className="flex flex-col items-center py-1 text-gray-400 dark:text-white/40">
      <div className="h-4 w-px bg-gray-300 dark:bg-white/25" />
      <ArrowDown className="h-4 w-4" />
    </div>
  );
}

export default function GymEmailAutomationFlowCanvas({
  triggerCode,
  triggerLabel,
  triggerConfig = {},
  conditions = [],
  steps = [],
  tags = [],
  templates = [],
  meta = {},
}) {
  const { t } = useTranslation();

  const stepIcon = (type) => {
    if (type === 'email.send') return Mail;
    if (type === 'wait') return Pause;
    if (type === 'stop') return Square;
    if (type === 'tag.add' || type === 'tag.remove') return Tag;
    return GitBranch;
  };

  const triggerSubtitle = formatAutomationTriggerSummary({
    triggerCode,
    triggerConfig,
    triggerLabel,
    tags,
    t,
  });

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-black/20 p-4 overflow-x-auto">
      <p className={`${gymSocialMutedText} text-xs mb-3`}>{t('gym.email.automations.flowHint')}</p>
      <div className="flex flex-col items-center min-w-[240px]">
        <FlowNode icon={GitBranch} title={t('gym.email.automations.flowTrigger')} subtitle={triggerSubtitle} tone="trigger" />
        {conditions.length > 0 && (
          <>
            <Connector />
            <div className="flex flex-col items-center gap-2 w-full max-w-md">
              <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-white/50">{t('gym.email.automations.flowConditions')}</p>
              {conditions.map((cond, i) => (
                <FlowNode key={i} title={formatAutomationConditionLabel(cond, meta, tags)} tone="condition" />
              ))}
            </div>
          </>
        )}
        {steps.map((step, index) => (
          <React.Fragment key={index}>
            <Connector />
            <FlowNode
              icon={stepIcon(step.action_type)}
              title={`${index + 1}. ${formatAutomationStepLabel(step, meta, tags, templates, t)}`}
              tone={step.action_type === 'stop' ? 'stop' : 'action'}
            />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
